import { ServiceLayout } from "@/components/ServiceLayout"
import { cognitiveCloudFAQs } from "@/data/faqData"

const CognitiveCloud = () => {
  return (
    <ServiceLayout
      title="Cognitive Cloud Infrastructure"
      description="Architecting self-healing cloud environments on AWS/Azure that automatically scale resources based on predictive traffic modeling."
      heroImage="https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&q=80&w=2600"
      faqs={cognitiveCloudFAQs}
      faqTitle="Cognitive Cloud FAQs"
      faqSubtitle="Common questions about intelligent cloud architecture and automation"
      features={[
        {
          title: "Predictive Autoscaling",
          description:
            "Scaling up resources *before* traffic spikes hit by analyzing historical patterns and marketing calendars.",
        },
        {
          title: "Serverless Architecture",
          description:
            "Removing infrastructure overhead by deploying logic as functions (Lambda/Azure Functions) that charge only for compute time.",
        },
        {
          title: "Multi-Cloud Strategy",
          description:
            "Avoiding vendor lock-in with containerized workloads (Kubernetes) that can migrate seamlessly between providers.",
        },
      ]}
      approach={{
        title: "Anti-Fragile Infrastructure",
        description:
          "We architect for 'Anti-Fragility' - systems that get stronger under stress.",
        image:
          "https://images.unsplash.com/photo-1558494949-ef2bb6affa0b?auto=format&fit=crop&q=80&w=2600",
        content: [
          "We architect for 'Anti-Fragility' - building systems that get stronger under stress. Unlike traditional static infrastructure, our cloud environments are designed to breathe.",
          "We utilize stochastic modeling to predict traffic patterns and pre-scale resources before demand spikes suitable. This 'Predictive Autoscaling' eliminates the latency of reactive scaling policies.",
          "Chaos Engineering is core to our delivery. We intentionally introduce faults into the system during testing - killing zones, severing database connections - to ensure that self-healing mechanisms trigger correctly. If your cloud can survive our tests, it can survive Black Friday.",
        ],
      }}
      benefits={[
        "30% Reduction in Cloud Spend",
        "99.999% Availability SLA",
        "Zero-Downtime Deployments",
        "Automated Disaster Recovery",
        "Infrastructure as Code (IaC)",
        "Real-Time Cost Monitoring",
      ]}
      process={{
        steps: [
          {
            title: "Cloud Readiness Assessment",
            description:
              "Evaluating your current infrastructure and application portfolio to determine the optimal migration strategy (Rehost, Replatform, Refactor).",
          },
          {
            title: "Landing Zone Setup",
            description:
              "Architecting a secure, compliant foundation with proper network segmentation, identity management, and governance policies.",
          },
          {
            title: "Migration & Modernization",
            description:
              "Executing the migration in waves, prioritizing critical workloads and refactoring monoliths into microservices where applicable.",
          },
          {
            title: "Continuous Optimization",
            description:
              "Implementing FinOps practices and autoscaling policies to ensure you only pay for the resources you actually need.",
          },
        ],
      }}
      techStack={[
        {
          category: "Cloud Providers",
          items: ["AWS", "Azure", "Google Cloud", "DigitalOcean"],
        },
        {
          category: "Infrastructure as Code",
          items: ["Terraform", "CloudFormation", "Pulumi", "Ansible"],
        },
        {
          category: "Containerization",
          items: ["Docker", "Kubernetes (EKS/AKS/GKE)", "Helm", "Istio"],
        },
      ]}
    />
  )
}

export default CognitiveCloud
